import type { ReactNode } from 'react'
import clsx from 'clsx'

import { PageLayout } from '@components/layouts/PageLayout'

import { docsLayout } from '../components/DocsLayout'
import { Button } from '@atoms'
import Icon, { type IconTypes } from '@components/utils/Icon'

type Tier = 'page' | 'primary' | 'secondary' | 'chrome'
type PinTone = 'accent' | 'amber' | 'slate'

const TIER_LABEL: Record<Tier, string> = {
  page: 'Page level',
  primary: 'Primary nav',
  secondary: 'Secondary nav',
  chrome: 'View chrome',
}

const TIER_CLASS: Record<Tier, string> = {
  page: 'tw:bg-gray-cool-100 tw:text-gray-cool-700 tw:border tw:border-gray-cool-200',
  primary: 'tw:bg-primary-600 tw:text-white',
  secondary: 'tw:bg-primary-50 tw:text-primary-700 tw:border tw:border-primary-200',
  chrome: 'tw:bg-warning-50 tw:text-warning-700 tw:border tw:border-warning-200',
}

type NavElement = {
  n: number
  pin: PinTone
  name: string
  code: string
  tier: Tier
  what: string
  when: string
}

const ELEMENTS: NavElement[] = [
  {
    n: 1,
    pin: 'slate',
    name: 'Page Header',
    code: 'PageHeader',
    tier: 'page',
    what: 'The identity band for the whole record — group name, a one-line subtitle, and the page’s single most important action.',
    when: 'You need to say what this page is and offer the one top action for the whole record.',
  },
  {
    n: 2,
    pin: 'accent',
    name: 'Page header nav',
    code: 'PageHeaderNav',
    tier: 'primary',
    what: 'The top-level section switcher. The only bar that changes what the whole content area below it shows.',
    when: 'Splitting a record into a small set (≈3–6) of peer sections, each big enough to own the screen.',
  },
  {
    n: 3,
    pin: 'accent',
    name: 'Tab Menu',
    code: 'TabItem dropdown',
    tier: 'primary',
    what: 'An optional dropdown hung off a single tab that jumps straight into one of its sub-views.',
    when: 'A tab has named sub-views you want reachable from the top bar without entering the tab first.',
  },
  {
    n: 6,
    pin: 'accent',
    name: 'Sub Nav',
    code: 'CommsSubNav',
    tier: 'secondary',
    what: 'A secondary switcher inside one tab that toggles between its mutually-exclusive views. Styled lighter than the main tabs.',
    when: 'One tab holds several closely-related views that shouldn’t each be promoted to a top-level tab.',
  },
  {
    n: 4,
    pin: 'amber',
    name: 'View Action Bar',
    code: 'ViewActionBar',
    tier: 'chrome',
    what: 'The header at the top of a tab’s content: view title/description on the left (both optional), that view’s actions on the right. Not navigation — it names the current view and hosts its actions.',
    when: 'You need a consistent per-view anchor for a tab’s title and its context-specific actions.',
  },
  {
    n: 5,
    pin: 'amber',
    name: 'Actions Menu',
    code: 'DropdownMenu',
    tier: 'chrome',
    what: 'The overflow dropdown that collects a view’s secondary, less-frequent actions.',
    when: 'A view has more actions than belong on screen — keep the primary a button, fold the rest away.',
  },
]

const PIN_CLASS: Record<PinTone, string> = {
  accent: 'tw:bg-primary-600 tw:text-white',
  amber: 'tw:bg-warning-600 tw:text-white',
  slate: 'tw:bg-gray-cool-500 tw:text-white',
}

function Pin({ n, tone = 'accent', className }: { n: number; tone?: PinTone; className?: string }) {
  return (
    <span
      className={clsx(
        'tw:inline-flex tw:h-6 tw:w-6 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-xs tw:font-semibold',
        PIN_CLASS[tone],
        className
      )}
    >
      {n}
    </span>
  )
}

function TierBadge({ tier }: { tier: Tier }) {
  return (
    <span
      className={clsx(
        'tw:inline-block tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide',
        TIER_CLASS[tier]
      )}
    >
      {TIER_LABEL[tier]}
    </span>
  )
}

function DocSection({ title, intro, children }: { title: string; intro?: string; children: ReactNode }) {
  return (
    <section className="tw:flex tw:flex-col tw:gap-4">
      <div>
        <h2 className="tw:text-lg tw:font-bold tw:text-gray-cool-900">{title}</h2>
        {intro && <p className="tw:mt-1 tw:max-w-prose tw:text-sm tw:text-gray-cool-600">{intro}</p>}
      </div>
      {children}
    </section>
  )
}

function WireBand({ pin, tone = 'accent', children }: { pin?: number; tone?: PinTone; children: ReactNode }) {
  return (
    <div className="tw:flex tw:items-center tw:gap-3">
      {pin ? <Pin n={pin} tone={tone} /> : <span className="tw:h-6 tw:w-6 tw:shrink-0" />}
      <div className="tw:min-w-0 tw:flex-1">{children}</div>
    </div>
  )
}

function Wireframe() {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white tw:p-4 tw:shadow-xs">
      <WireBand pin={1} tone="slate">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-dashed tw:border-gray-cool-200 tw:pb-3">
          <div className="tw:flex tw:items-center tw:gap-2.5">
            <span className="tw:flex tw:h-9 tw:w-9 tw:items-center tw:justify-center tw:rounded-lg tw:bg-primary-50 tw:text-primary-600">
              <Icon type="users-01" className="tw:h-5 tw:w-5" aria-hidden />
            </span>
            <div>
              <div className="tw:text-sm tw:font-semibold tw:text-gray-cool-900">North Ward Volunteers</div>
              <div className="tw:text-xs tw:text-gray-cool-400">Riverside, District 4 · 248 supporters</div>
            </div>
          </div>
          <div className="tw:flex tw:gap-2">
            <span className="tw:rounded-lg tw:border tw:border-gray-cool-200 tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-gray-cool-600">
              Analytics
            </span>
            <span className="tw:rounded-lg tw:bg-primary-600 tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-white">
              + Add new supporter
            </span>
          </div>
        </div>
      </WireBand>

      <WireBand pin={2}>
        <div className="tw:flex tw:flex-wrap tw:items-stretch tw:border-b tw:border-gray-cool-200">
          {[
            { label: 'Group Details' },
            { label: 'Supporters', count: '248' },
            { label: 'Lists', count: '6' },
            { label: 'Events', count: '3' },
          ].map((t) => (
            <span
              key={t.label}
              className="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-3 tw:py-2 tw:text-xs tw:font-medium tw:text-gray-cool-600"
            >
              {t.label}
              {t.count && (
                <span className="tw:rounded-full tw:bg-gray-cool-100 tw:px-1.5 tw:text-[10px] tw:text-gray-cool-500">
                  {t.count}
                </span>
              )}
            </span>
          ))}
          <span className="tw:-mb-px tw:inline-flex tw:items-center tw:gap-1.5 tw:border-b-2 tw:border-primary-600 tw:px-3 tw:py-2 tw:text-xs tw:font-semibold tw:text-primary-700">
            Communications
            <span className="tw:rounded-full tw:bg-warning-50 tw:px-1.5 tw:text-[10px] tw:font-semibold tw:text-warning-700">7</span>
            <Icon type="chevron-down" className="tw:h-3 tw:w-3" aria-hidden />
          </span>
        </div>
      </WireBand>

      <WireBand pin={3}>
        <div className="tw:ml-auto tw:w-full tw:max-w-xs tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white tw:p-1.5 tw:shadow-md">
          {[
            { icon: 'message-chat-square', label: 'Inbox', desc: 'Email & SMS conversations.' },
            { icon: 'mail-01', label: 'Email blasts', desc: 'Bulk emails sent to this group.' },
            { icon: 'send', label: 'SMS blasts', desc: 'Bulk texts sent to this group.' },
          ].map((item, i) => (
            <div
              key={item.label}
              className={clsx(
                'tw:flex tw:items-start tw:gap-2.5 tw:rounded-lg tw:p-2',
                i === 0 && 'tw:bg-gray-cool-50'
              )}
            >
              <span className="tw:mt-0.5 tw:flex tw:h-6 tw:w-6 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:bg-primary-50 tw:text-primary-600">
                <Icon type={item.icon as IconTypes} className="tw:h-3.5 tw:w-3.5" aria-hidden />
              </span>
              <span className="tw:min-w-0">
                <span className="tw:block tw:text-xs tw:font-semibold tw:text-gray-cool-900">{item.label}</span>
                <span className="tw:block tw:text-[10px] tw:text-gray-cool-400">{item.desc}</span>
              </span>
            </div>
          ))}
        </div>
      </WireBand>

      <WireBand pin={4} tone="amber">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-gray-cool-200 tw:pb-3">
          <div>
            <div className="tw:text-sm tw:font-semibold tw:text-gray-cool-900">Communications</div>
            <div className="tw:text-xs tw:text-gray-cool-400">Inbox and broadcasts for this group.</div>
          </div>
          <div className="tw:flex tw:items-center tw:gap-2">
            <span className="tw:rounded-lg tw:border tw:border-gray-cool-200 tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-gray-cool-600">
              Open full inbox
            </span>
            <span className="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-lg tw:border tw:border-gray-cool-200 tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-gray-cool-600">
              Actions <Pin n={5} tone="amber" className="tw:h-4 tw:w-4 tw:text-[10px]" />
              <Icon type="chevron-down" className="tw:h-3 tw:w-3" aria-hidden />
            </span>
          </div>
        </div>
      </WireBand>

      <WireBand pin={6}>
        <div className="tw:inline-flex tw:w-fit tw:max-w-full tw:flex-wrap tw:gap-1 tw:rounded-lg tw:border tw:border-gray-cool-100 tw:bg-gray-cool-50 tw:p-1">
          <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-md tw:bg-white tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-primary-700 tw:shadow-xs">
            <Icon type="message-chat-square" className="tw:h-3.5 tw:w-3.5" aria-hidden /> Inbox
            <span className="tw:rounded-full tw:bg-warning-50 tw:px-1.5 tw:text-[10px] tw:font-semibold tw:text-warning-700">7</span>
          </span>
          <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-gray-cool-600">
            <Icon type="mail-01" className="tw:h-3.5 tw:w-3.5" aria-hidden /> Email blasts
          </span>
          <span className="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-gray-cool-600">
            <Icon type="send" className="tw:h-3.5 tw:w-3.5" aria-hidden /> SMS blasts
          </span>
        </div>
      </WireBand>

      <WireBand>
        <div className="tw:flex tw:flex-col tw:gap-2 tw:pt-1">
          <div className="tw:h-2 tw:w-4/5 tw:rounded tw:bg-gray-cool-100" />
          <div className="tw:h-2 tw:w-3/5 tw:rounded tw:bg-gray-cool-100" />
        </div>
      </WireBand>
    </div>
  )
}

function Legend() {
  const items: Array<{ tone: string; label: string }> = [
    { tone: 'tw:bg-gray-cool-500', label: 'Page level' },
    { tone: 'tw:bg-primary-600', label: 'Navigation' },
    { tone: 'tw:bg-warning-600', label: 'View chrome (labels & actions, not nav)' },
  ]
  return (
    <div className="tw:flex tw:flex-wrap tw:gap-x-5 tw:gap-y-1.5 tw:text-xs tw:text-gray-cool-600">
      {items.map((it) => (
        <span key={it.label} className="tw:inline-flex tw:items-center tw:gap-1.5">
          <span className={clsx('tw:h-3 tw:w-3 tw:rounded', it.tone)} />
          {it.label}
        </span>
      ))}
    </div>
  )
}

function ReferenceTable() {
  return (
    <div className="tw:overflow-x-auto tw:rounded-xl tw:border tw:border-gray-cool-200 tw:shadow-xs">
      <table className="tw:w-full tw:min-w-176 tw:border-collapse tw:bg-white tw:text-left tw:text-sm">
        <thead>
          <tr className="tw:bg-gray-cool-50 tw:text-xs tw:uppercase tw:tracking-wide tw:text-gray-cool-500">
            <th className="tw:px-4 tw:py-3 tw:font-semibold">Element</th>
            <th className="tw:px-4 tw:py-3 tw:font-semibold">What it is</th>
            <th className="tw:px-4 tw:py-3 tw:font-semibold">Reach for it when…</th>
          </tr>
        </thead>
        <tbody>
          {ELEMENTS.map((el) => (
            <tr key={el.code} className="tw:border-t tw:border-gray-cool-100 tw:align-top">
              <td className="tw:px-4 tw:py-4 tw:min-w-48">
                <div className="tw:mb-1.5 tw:flex tw:items-center tw:gap-2">
                  <Pin n={el.n} tone={el.pin} />
                  <span className="tw:font-semibold tw:text-gray-cool-900">{el.name}</span>
                </div>
                <code className="tw:rounded tw:bg-primary-50 tw:px-1.5 tw:py-0.5 tw:font-mono tw:text-xs tw:text-primary-700">
                  {el.code}
                </code>
                <div className="tw:mt-2">
                  <TierBadge tier={el.tier} />
                </div>
              </td>
              <td className="tw:px-4 tw:py-4 tw:text-gray-cool-600">{el.what}</td>
              <td className="tw:px-4 tw:py-4 tw:text-gray-cool-600">{el.when}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ConfusablePair() {
  return (
    <div className="tw:rounded-xl tw:border tw:border-gray-cool-200 tw:border-l-4 tw:border-l-primary-600 tw:bg-white tw:p-5 tw:shadow-xs">
      <h3 className="tw:mb-3 tw:text-base tw:font-bold tw:text-gray-cool-900">Tab Menu vs. Sub Nav</h3>
      <div className="tw:grid tw:gap-4 tw:sm:grid-cols-2">
        <div>
          <h4 className="tw:mb-1 tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-semibold tw:text-gray-cool-900">
            <Pin n={3} /> Tab Menu — a way in
          </h4>
          <p className="tw:text-sm tw:text-gray-cool-600">
            Lives in the top tabs bar. A shortcut: pick an item and it opens the tab and lands you on that
            sub-view. Ignore it and you can still reach everything.
          </p>
        </div>
        <div>
          <h4 className="tw:mb-1 tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-semibold tw:text-gray-cool-900">
            <Pin n={6} /> Sub Nav — the switch inside
          </h4>
          <p className="tw:text-sm tw:text-gray-cool-600">
            Lives inside the open tab. The actual control for moving between that tab’s views, always present
            while the tab is open.
          </p>
        </div>
      </div>
      <p className="tw:mt-4 tw:border-t tw:border-gray-cool-100 tw:pt-3 tw:text-sm tw:text-gray-cool-600">
        They’re kept in lockstep in code — both driven by the same{' '}
        <code className="tw:font-mono tw:text-xs tw:text-primary-700">COMMS_SUB_ITEMS</code> list — so the
        shortcut and the in-tab switch never disagree about which sub-views exist.
      </p>
    </div>
  )
}

type ActionLevel = { name: string; blurb: string; button: ReactNode }
const ACTION_LEVELS: ActionLevel[] = [
  {
    name: 'Primary action',
    blurb: 'The single most important action in this context. One filled accent button — never two competing.',
    button: (
      <Button rank="primary" leadingIcon="plus">
        Add new supporter
      </Button>
    ),
  },
  {
    name: 'Secondary action',
    blurb: 'A common but not primary action. Lower-emphasis, outlined. Zero or more per context.',
    button: (
      <Button rank="secondary" leadingIcon="line-chart-up">
        Analytics
      </Button>
    ),
  },
  {
    name: 'Overflow',
    blurb: 'Everything else, folded behind the Actions Menu element above.',
    button: (
      <Button rank="secondary" leadingIcon="dots-horizontal" trailingIcon="chevron-down">
        Actions
      </Button>
    ),
  },
]

function ActionsPanel() {
  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:grid tw:gap-4 tw:sm:grid-cols-3">
        {ACTION_LEVELS.map((a) => (
          <div
            key={a.name}
            className="tw:flex tw:flex-col tw:gap-3 tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white tw:p-4 tw:shadow-xs"
          >
            <div className="tw:flex tw:min-h-10 tw:items-center">{a.button}</div>
            <div>
              <div className="tw:text-sm tw:font-semibold tw:text-gray-cool-900">{a.name}</div>
              <p className="tw:mt-0.5 tw:text-xs tw:text-gray-cool-600">{a.blurb}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="tw:rounded-lg tw:bg-gray-cool-50 tw:p-4 tw:text-sm tw:text-gray-cool-600">
        <span className="tw:font-semibold tw:text-gray-cool-900">Where they live.</span> Page-level actions sit in
        the Page Header (top-right); actions specific to the current view sit in that view’s View Action Bar
        (top-right). Whichever bar hosts them, the primary stays one filled button and the rest collapse into an
        Actions menu — one primary per context.
      </div>
    </div>
  )
}

const CONVENTIONS: Array<{ title: string; body: string }> = [
  {
    title: 'Breadcrumbs live in one fixed place',
    body: 'SidebarLayout renders the Breadcrumbs bar directly above the page header on every page, so their position never changes. Consistent placement is what makes them learnable — don’t relocate them per page.',
  },
  {
    title: 'The Page Header description is optional',
    body: 'Use the subtitle for at-a-glance metadata (location · supporter count) when it helps, and drop it when it doesn’t. It’s separate from the breadcrumb — don’t use a description to do a breadcrumb’s job.',
  },
  {
    title: 'One primary action per context',
    body: 'The page gets one; each view gets one. Page-level actions live in the Page Header, view-specific actions in the View Action Bar, and everything else folds into an Actions menu.',
  },
  {
    title: 'A bar of actions still needs an anchor',
    body: 'The View Action Bar’s title and description are optional, but right-aligned buttons look unanchored on their own. If you drop the title, left-align the actions or keep a short label so the row still reads as one unit.',
  },
]

function ConventionsList() {
  return (
    <div className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-cool-100 tw:rounded-xl tw:border tw:border-gray-cool-200 tw:bg-white">
      {CONVENTIONS.map((c) => (
        <div key={c.title} className="tw:flex tw:flex-col tw:gap-1 tw:p-4 tw:sm:flex-row tw:sm:gap-6">
          <div className="tw:text-sm tw:font-semibold tw:text-gray-cool-900 tw:sm:w-60 tw:sm:shrink-0">
            {c.title}
          </div>
          <p className="tw:text-sm tw:text-gray-cool-600">{c.body}</p>
        </div>
      ))}
    </div>
  )
}

function OpenQuestion() {
  return (
    <div className="tw:rounded-xl tw:border tw:border-warning-200 tw:bg-warning-50 tw:p-5">
      <h3 className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-bold tw:text-warning-700">
        <Icon type="info-circle" className="tw:h-4 tw:w-4" aria-hidden />
        Open question — secondary nav across layouts
      </h3>
      <p className="tw:text-sm tw:text-warning-700">
        The Communications sub-nav (the second row) may not sit comfortably in every layout, and can be awkward to
        implement consistently. The Tab Menu mirrors the same links, so any sub-view is reachable without the second
        row. Still to decide: is the row, the dropdown, or both the canonical entry? The secondary nav needn’t be a
        “nav bar” at all — but some form of primary and secondary navigation is needed on these pages.
      </p>
    </div>
  )
}

const NavReference = () => {
  return (
    <>
      <PageLayout.Header icon="menu-01" heading="Navigation reference" />
      <PageLayout.Body>
        <div className="tw:mx-auto tw:flex tw:max-w-5xl tw:flex-col tw:gap-10 tw:pb-16">
          <header className="tw:flex tw:flex-col tw:gap-2 tw:border-b tw:border-gray-cool-200 tw:pb-6">
            <h1 className="tw:max-w-2xl tw:text-2xl tw:font-bold tw:text-gray-cool-900">
              One name for every navigation piece on the group page
            </h1>
            <p className="tw:max-w-prose tw:text-sm tw:text-gray-cool-600">
              A shared vocabulary so design and engineering point at the same thing with the same word — and
              know which control to reach for. Every element below is real and named in the group-show design
              preview.
            </p>
            <div className="tw:mt-1 tw:flex tw:flex-wrap tw:gap-2 tw:text-xs">
              {['source · GroupShowPreview.tsx', 'PR #6850', 'status · design preview (WIP)'].map((tag) => (
                <span
                  key={tag}
                  className="tw:rounded-md tw:border tw:border-gray-cool-100 tw:bg-gray-cool-50 tw:px-2 tw:py-1 tw:font-mono tw:text-gray-cool-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <DocSection
            title="Where each one lives"
            intro="The group page, shown with the Communications tab active so every element is visible at once. Numbers match the reference table below."
          >
            <Wireframe />
            <Legend />
          </DocSection>

          <DocSection
            title="The reference"
            intro="The first three elements navigate (they change what you see); the last two are view chrome (they label the current view and hold its actions). One primary nav per page; secondary nav and view chrome repeat per tab."
          >
            <ReferenceTable />
          </DocSection>

          <DocSection
            title="Actions: primary, secondary & overflow"
            intro="The action buttons the bars hold. They aren’t navigation, but they’re the elements you’ll name most — and they can live in two places, so the rule for where matters."
          >
            <ActionsPanel />
          </DocSection>

          <DocSection
            title="The one that trips people up"
            intro="Tab Menu and Sub Nav both point at a tab’s sub-views, so they get conflated. The difference is where they live and what they do."
          >
            <ConfusablePair />
          </DocSection>

          <DocSection
            title="Conventions & decisions"
            intro="Cross-cutting rules that keep pages consistent — the same thing should always be in the same place, because that consistency is what makes the layout learnable."
          >
            <ConventionsList />
            <OpenQuestion />
          </DocSection>

          <p className="tw:border-t tw:border-gray-cool-200 tw:pt-5 tw:text-sm tw:text-gray-cool-400">
            All six are currently defined inline in GroupShowPreview.tsx on the Radix Tabs primitive. When the
            design settles, the shared home is the Tabs atom (app/frontend/components/atoms/Tabs.tsx), which
            also brings URL-syncing for deep links. Names here are proposals for the shared vocabulary — the
            editable long-form version lives in docs/design-system/.
          </p>
        </div>
      </PageLayout.Body>
    </>
  )
}

NavReference.layout = docsLayout

export default NavReference
